import React from "react";
import { StandardPageWrapper } from "../../../components/layout/StandardPageWrapper";
import { PageHeader } from "../../../components/layout/header/Header";
import {
  DashboardCompletionStepBox,
  ExpandablePanel,
  ToolTip,
  VerticalProgressBarCirclesWithText,
} from "@truevault/aquilo";
import { Routes } from "../../../root/routes";
import { usePrimaryOrganizationId } from "../../../hooks";
import {
  isPrivacyCenterOnboardingComplete,
  isWorkflowsOnboardingComplete,
  TruevaultHrOnboardingProgress,
  TruevaultOnboardingProgress,
  useOnboardingProgress,
} from "../../../../common/hooks/useOnboardingProgress";

export const DevInstructionsFinishedHrDataMapSlug = "finished-hr-data-map";

// HR Center is enabled after both Privacy Center and DSAR Workflows onboarding are complete (or if any of the HR onboarding steps are already complete)
export const isHrCenterEnabled = (onboardingProgress: TruevaultOnboardingProgress) => {
  let isAnyHrStepComplete =
    onboardingProgress?.hrCenterOnboardingProgress?.isFinishedWithHrDataMap ||
    onboardingProgress?.hrCenterOnboardingProgress?.isFinishedWithHrRetentionPolicy ||
    onboardingProgress?.hrCenterOnboardingProgress?.isHrNoticesPublished;
  if (isAnyHrStepComplete) {
    return true;
  }

  return (
    isPrivacyCenterOnboardingComplete(onboardingProgress.privacyCenterOnboardingProgress) &&
    isWorkflowsOnboardingComplete(onboardingProgress.workflowsSetupOnboardingProgress)
  );
};

export const isHrNoticesEnabled = (hrCenterOnboardingProgress: TruevaultHrOnboardingProgress) => {
  let { isFinishedWithHrDataMap, isFinishedWithHrRetentionPolicy, isHrNoticesPublished } =
    hrCenterOnboardingProgress;
  return (isFinishedWithHrDataMap && isFinishedWithHrRetentionPolicy) || isHrNoticesPublished;
};

export const isHrRetentionPolicyEnabled = (
  hrCenterOnboardingProgress: TruevaultHrOnboardingProgress,
) => {
  let { isFinishedWithHrDataMap, isFinishedWithHrRetentionPolicy } = hrCenterOnboardingProgress;
  return isFinishedWithHrDataMap || isFinishedWithHrRetentionPolicy;
};

export const HRCenterDashboardPage = () => {
  const orgId = usePrimaryOrganizationId();

  const [onboardingProgress] = useOnboardingProgress(orgId);
  let {
    isFinishedWithHrDataMap = false,
    isFinishedWithHrRetentionPolicy = false,
    isHrNoticesPublished = false,
  } = onboardingProgress?.hrCenterOnboardingProgress || {};

  let isHrRetentionPolicyItemEnabled = isHrRetentionPolicyEnabled(
    onboardingProgress?.hrCenterOnboardingProgress,
  );
  let isHrNoticesItemEnabled = isHrNoticesEnabled(onboardingProgress?.hrCenterOnboardingProgress);

  let hrDataMapItem = (
    <DashboardCompletionStepBox
      iconFilename="/assets/images/stay_compliant/hr_center/hr-retention-policy-icon.svg"
      title="Data Map"
      description="Review HR Vendors, collection groups and disclosures"
      status={isFinishedWithHrDataMap ? "complete" : "finish-set-up"}
    />
  );

  let hrRetentionPolicyItem = (
    <DashboardCompletionStepBox
      iconFilename="/assets/images/stay_compliant/hr_center/hr-retention-policy-icon.svg"
      title="Retention Policy"
      description="Set up your company's HR retention policy"
      status={isFinishedWithHrRetentionPolicy ? "complete" : "finish-set-up"}
      isDimmed={!isHrRetentionPolicyItemEnabled}
    />
  );

  let hrNoticesItem = (
    <DashboardCompletionStepBox
      iconFilename="/assets/images/stay_compliant/hr_center/hr-notices-icon.svg"
      title="Notices"
      description="Review your HR Notices and where to post them"
      status={isHrNoticesPublished ? "complete" : "finish-set-up"}
      isDimmed={!isHrNoticesItemEnabled}
    />
  );

  return (
    <StandardPageWrapper fullWidth>
      <PageHeader titleContent="HR Center" />

      <div className="tw-boundary">
        {(!isFinishedWithHrDataMap ||
          !isFinishedWithHrRetentionPolicy ||
          !isHrNoticesPublished) && (
          <ExpandablePanel title="Finish TrueVault Set Up" titleFontSize="2xl">
            <>
              <p className="font-normal text-[18px] text-neutral-800 mt-6">
                Complete the steps below to finish and publish your Privacy Center.
              </p>
              <div className="mt-6">
                <VerticalProgressBarCirclesWithText
                  steps={[
                    {
                      name: "HR Data Map",
                      description: "Set up your company's HR data map",
                      status: isFinishedWithHrDataMap ? "complete" : "current",
                      route: Routes.hrCenter.hrDataMap.root,
                    },
                    {
                      name: "HR Retention Policy",
                      description: "Set up your company's HR retention policy",
                      status: isFinishedWithHrRetentionPolicy
                        ? "complete"
                        : isFinishedWithHrDataMap
                        ? "current"
                        : "upcoming",
                      route: Routes.hrCenter.editHrRetentionPolicy,
                    },
                    {
                      name: "Publish HR Notices",
                      description: "Connect your site's address",
                      status: isHrNoticesPublished
                        ? "complete"
                        : isFinishedWithHrDataMap && isFinishedWithHrRetentionPolicy
                        ? "current"
                        : "upcoming",
                      route: Routes.hrCenter.hrNotices,
                    },
                  ]}
                />
              </div>
            </>
          </ExpandablePanel>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
            maxWidth: "830px",
            marginTop: "48px",
          }}
        >
          <a href={Routes.hrCenter.hrDataMap.root}>{hrDataMapItem}</a>

          {isHrRetentionPolicyItemEnabled ? (
            <a href={Routes.hrCenter.editHrRetentionPolicy}>{hrRetentionPolicyItem}</a>
          ) : (
            <ToolTip
              text={"Complete previous steps to view this step"}
              width="190px"
              xOffset={-60}
              yOffset={100}
            >
              {hrRetentionPolicyItem}
            </ToolTip>
          )}

          {isHrNoticesItemEnabled ? (
            <a href={Routes.hrCenter.hrNotices}>{hrNoticesItem}</a>
          ) : (
            <ToolTip
              text={"Complete previous step to view this step"}
              width="190px"
              xOffset={-60}
              yOffset={100}
            >
              {hrNoticesItem}
            </ToolTip>
          )}
        </div>
      </div>
    </StandardPageWrapper>
  );
};
